'use client';

import { useEffect, useState } from 'react';

import { getRaffleOffers, type RaffleOffersResponse } from '@/actions/raffleOffers';
import type { PromotionOffer } from '@/utils';

export type PromotionOffersState =
  | { status: 'loading' }
  | { status: 'legacy' }
  | {
      status: 'unified';
      offers: PromotionOffer[];
      ticketPrice: number | null;
      isOnSale: boolean;
      raffleName: string;
    }
  | { status: 'error' };

type UsePromotionOffersParams = {
  raffleId: string | undefined;
  initialData?: RaffleOffersResponse;
};

function toPromotionOffersState(response: RaffleOffersResponse): PromotionOffersState {
  if (response.enabled) {
    return {
      status: 'unified',
      offers: response.offers,
      ticketPrice: response.ticketPrice,
      isOnSale: response.isOnSale,
      raffleName: response.raffleName,
    };
  }

  return { status: 'legacy' };
}

export const usePromotionOffers = ({
  raffleId,
  initialData,
}: UsePromotionOffersParams): PromotionOffersState => {
  const initialState: PromotionOffersState = initialData
    ? toPromotionOffersState(initialData)
    : { status: 'loading' };

  const [state, setState] = useState<PromotionOffersState>(initialState);

  useEffect(() => {
    if (initialData) return;
    if (!raffleId) return;

    let isActive = true;

    const fetchOffers = async () => {
      setState({ status: 'loading' });

      const response = await getRaffleOffers(raffleId);

      if (!isActive) return;

      if (!response) {
        setState({ status: 'error' });
        return;
      }

      const nextState = toPromotionOffersState(response);
      setState(nextState);
    };

    fetchOffers();

    return () => {
      isActive = false;
    };
  }, [raffleId, initialData]);

  return state;
};
